/**
 * frontend/src/pages/valuation/_valuationIndustryMap.ts
 *
 * VAL-NAV-STATE-1 R2 — the engagement's signed industry profile
 * (`engagement.industry`, one of exactly 5 codes: accounting, fintech,
 * generic, hvac, plumbing — `core/valuation/profile_registry.py`) and B3's
 * benchmark table (`core/valuation/config_seed_benchmark.py`, 12 rows keyed
 * by `benchmark_key`) are two different, unrelated catalogs — confirmed in
 * `apps/api/routers/_valuation_draft.py`'s own docstring. No field on the
 * wire (`GET /api/v1/valuation/industries`) exposes a row's `signed_profile`,
 * so there is no way to derive `benchmark_key` from `engagement.industry`
 * without duplicating that mapping client-side. This file is that mapping.
 *
 * Source of truth being mirrored — re-derive this map if either changes:
 *   - `core/valuation/config_seed_benchmark.py` — there are now four signed
 *     pairings across four rows (hvac and plumbing still share one row).
 *   - `apps/api/routers/_valuation_finalize_deps.py` — `_profiles_are_coherent()`
 *     is what actually enforces this pairing server-side at finalize; this
 *     map exists so Tuning's screen picks a row that will already be
 *     coherent, not to duplicate the enforcement itself.
 *
 * "generic" has no signed row of its own. `_profiles_are_coherent()` treats
 * "generic" as coherent with ANY unsigned benchmark row (there are 10), so
 * `GENERIC_DEFAULT_BENCHMARK` below is a defensible default among those 10,
 * not a resolved fact — `professional_services` is chosen as the broadest
 * unsigned row.
 */

const SIGNED_PROFILE_BENCHMARK: Record<string, string> = {
  hvac: 'trades_hvac_plumbing_electrical',
  plumbing: 'trades_hvac_plumbing_electrical',
  accounting: 'accounting_cpa_firm',
  fintech: 'money_services_msb',
  dental: 'dental_practice',
}

const GENERIC_DEFAULT_BENCHMARK = 'professional_services'

/** Resolve the B3 `benchmark_key` a signed industry profile should run
 * against. Falls back to the generic default for `generic`, for `null`, and
 * for any profile code this map does not (yet) recognize — never throws. */
export function benchmarkKeyForIndustry(industry: string | null | undefined): string {
  if (!industry) return GENERIC_DEFAULT_BENCHMARK
  return SIGNED_PROFILE_BENCHMARK[industry] ?? GENERIC_DEFAULT_BENCHMARK
}
